import { useEffect, useMemo, useState } from "react";
import localization from "../../../../assets/localization";

interface TypingIndicatorProps {
  toolName: string | null;
  language: string;
  // Bot bubbles are brand-colored (white text) for default assistants and
  // light for HM ones — the orb and label need to adapt.
  onDark: boolean;
}

// Concierge-style waiting indicator: a breathing brand orb plus a sequence
// of status lines driven by the SSE tool events. The sequence advances ONCE
// (slowly, with jitter so it doesn't feel metronomic) and holds on the last
// phrase — a looping script reads as fake, linear progress reads as work.
export default function TypingIndicator({
  toolName,
  language,
  onDark,
}: TypingIndicatorProps) {
  const t = localization.get(language);

  const phrases: string[] = useMemo(() => {
    const sequences = t.tool_status_sequences ?? {};
    if (toolName && sequences[toolName]) return sequences[toolName];
    if (toolName && t.tools_actions[toolName])
      return [t.tools_actions[toolName], ...t.status_generic];
    return t.status_thinking;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolName, language]);

  const [phraseIndex, setPhraseIndex] = useState(0);

  useEffect(() => {
    setPhraseIndex(0);
    if (phrases.length <= 1) return;

    let index = 0;
    let timer: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      timer = setTimeout(() => {
        index += 1;
        setPhraseIndex(index);
        if (index < phrases.length - 1) scheduleNext();
      }, 3800 + Math.random() * 2400);
    };
    scheduleNext();
    return () => clearTimeout(timer);
  }, [phrases]);

  return (
    <span className={`typing-indicator ${onDark ? "on-dark" : "on-light"}`}>
      <span className="typing-orb" />
      <span className="typing-status" key={`${phrases[0]}-${phraseIndex}`}>
        {phrases[Math.min(phraseIndex, phrases.length - 1)]}
      </span>
    </span>
  );
}
